import React, { useContext, useEffect, useMemo, useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import {
  ChatBubbleLeftRightIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../contexts/AuthContext';
import { parseFirestoreDate } from '../../utils/parseFirestoreDate';
import { reviewService } from './reviewService';
import { useGoogleReviewConfig } from './useGoogleReviewConfig';

function shortDate(value) {
  const d = parseFirestoreDate(value);
  if (!d) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function shortDateTime(value) {
  const d = parseFirestoreDate(value);
  if (!d) return null;
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sortDeliveryLog(log) {
  if (!Array.isArray(log)) return [];
  return [...log].sort((a, b) => {
    const ta = a?.at?.toMillis ? a.at.toMillis() : a?.at ? new Date(a.at).getTime() : 0;
    const tb = b?.at?.toMillis ? b.at.toMillis() : b?.at ? new Date(b.at).getTime() : 0;
    return tb - ta;
  });
}

function StatusPill({ ok, label, detail }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
        ok
          ? 'bg-green-50 text-green-800 ring-1 ring-inset ring-green-200'
          : 'bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200'
      }`}
      title={detail || label}
    >
      {label}
      {ok && detail ? <span className="font-normal text-green-700/80">· {detail}</span> : null}
    </span>
  );
}

function hasFeedbackContact(item) {
  return !!(String(item?.patientEmail || '').trim() || String(item?.consentId || '').trim());
}

function FeedbackCard({
  item,
  expanded,
  onToggleExpand,
  onAsk,
  onMarkRead,
  onDelete,
  onPreview,
  onOpenLog,
}) {
  const askedAt = shortDate(item.googleReviewAskedAt);
  const openedAt = shortDateTime(item.googleReviewEmailOpenedAt);
  const linkOpenedAt = shortDateTime(item.googleReviewLinkOpenedAt);
  const googleClickedAt = shortDateTime(item.googleReviewGoogleClickedAt);
  const deliveryLog = sortDeliveryLog(item.emailDeliveryLog);
  const comment = String(item.comment || '');
  const long = comment.length > 220;
  const shown = !long || expanded ? comment : `${comment.slice(0, 220).trim()}…`;
  const canContact = hasFeedbackContact(item);

  return (
    <article
      className={`rounded-xl border bg-white p-5 shadow-sm ${
        item.read ? 'border-gray-200' : 'border-amber-300 bg-amber-50/30'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">
              {item.patientName || (item.patientEmail ? 'Patient' : 'Anonymous')}
            </h3>
            {!item.read ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                New
              </span>
            ) : null}
            {!canContact ? (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                No contact
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-gray-500">
            {shortDate(item.createdAt) || '—'}
            {item.patientEmail ? ` · ${item.patientEmail}` : ''}
            {!item.patientEmail && item.consentId ? ' · Email via linked consent' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onDelete(item)}
          className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
          title="Delete feedback"
        >
          <TrashIcon className="h-3.5 w-3.5" />
          Delete
        </button>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">{shown}</p>
      {long ? (
        <button
          type="button"
          onClick={onToggleExpand}
          className="mt-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
        >
          {expanded ? 'Show less' : 'Show full message'}
        </button>
      ) : null}

      <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Google review request
        </p>
        {askedAt ? (
          <>
            <p className="mt-1 text-sm text-gray-700">
              Sent {askedAt}
              {item.googleReviewAskedTo ? ` to ${item.googleReviewAskedTo}` : ''}
              {(item.googleReviewAskedCount || 0) > 1
                ? ` · ${item.googleReviewAskedCount} sends`
                : ''}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <StatusPill
                ok={!!openedAt}
                label={openedAt ? 'Email opened' : 'Email not opened'}
                detail={openedAt ? shortDate(item.googleReviewEmailOpenedAt) : null}
              />
              <StatusPill
                ok={!!linkOpenedAt}
                label={linkOpenedAt ? 'Link opened' : 'Link not opened'}
                detail={linkOpenedAt ? shortDate(item.googleReviewLinkOpenedAt) : null}
              />
              <StatusPill
                ok={!!googleClickedAt}
                label={googleClickedAt ? 'Google clicked' : 'Google not clicked'}
                detail={googleClickedAt ? shortDate(item.googleReviewGoogleClickedAt) : null}
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              {item.googleReviewAskedHtml ? (
                <button
                  type="button"
                  onClick={() =>
                    onPreview({
                      to: item.googleReviewAskedTo,
                      subject: item.googleReviewAskedSubject || 'Google review request',
                      html: item.googleReviewAskedHtml,
                      at: item.googleReviewAskedAt,
                    })
                  }
                  className="inline-flex items-center gap-1 font-semibold text-indigo-700 hover:text-indigo-900"
                >
                  <EyeIcon className="h-3.5 w-3.5" />
                  View email
                </button>
              ) : null}
              {deliveryLog.length > 0 ? (
                <button
                  type="button"
                  onClick={() => onOpenLog(item)}
                  className="font-medium text-gray-600 hover:text-gray-900"
                >
                  Email log ({deliveryLog.length})
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <p className="mt-1 text-sm text-gray-500">
            {canContact
              ? 'No Google review email sent yet.'
              : 'No contact details — can’t reply or ask for a Google review.'}
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!item.read ? (
          <button
            type="button"
            onClick={() => onMarkRead(item)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Mark read
          </button>
        ) : null}
        {canContact ? (
          <button
            type="button"
            onClick={() => onAsk(item)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600"
          >
            <StarIcon className="h-3.5 w-3.5" />
            {askedAt ? 'Ask again' : 'Ask for Google review'}
          </button>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-500"
            title="This patient left no email. New private feedback requires an email."
          >
            <StarIcon className="h-3.5 w-3.5" />
            Ask unavailable
          </span>
        )}
      </div>
    </article>
  );
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'asked', label: 'Asked for Google' },
  { id: 'opened', label: 'Email opened' },
  { id: 'clicked', label: 'Google clicked' },
  { id: 'not_asked', label: 'Not asked yet' },
];

function isReviewListFeedback(item) {
  return item?.source === 'review_list' || !!String(item?.outreachContactId || '').trim();
}

export default function GoogleReviewFeedbackPanel({ clinicId, isReady, audience = 'reviews' }) {
  const { userData } = useContext(AuthContext);
  const { clinicName } = useGoogleReviewConfig(clinicId);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [askFor, setAskFor] = useState(null);
  const [askEmail, setAskEmail] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [logFor, setLogFor] = useState(null);

  useEffect(() => {
    if (!clinicId || !isReady) return undefined;
    setLoading(true);
    return reviewService.subscribeFeedback(
      clinicId,
      (list) => {
        setItems(list);
        setLoading(false);
      },
      100
    );
  }, [clinicId, isReady]);

  const scopedItems = useMemo(() => {
    if (audience === 'review-list') return items.filter(isReviewListFeedback);
    return items.filter((item) => !isReviewListFeedback(item));
  }, [items, audience]);

  const unread = scopedItems.filter((item) => !item.read).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scopedItems.filter((item) => {
      if (filter === 'unread' && item.read) return false;
      if (filter === 'asked' && !item.googleReviewAskedAt) return false;
      if (filter === 'opened' && !item.googleReviewEmailOpenedAt) return false;
      if (filter === 'clicked' && !item.googleReviewGoogleClickedAt) return false;
      if (filter === 'not_asked' && item.googleReviewAskedAt) return false;
      if (!q) return true;
      const hay = [
        item.patientName,
        item.patientEmail,
        item.googleReviewAskedTo,
        item.comment,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [scopedItems, search, filter]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  const openAskModal = (item) => {
    if (!hasFeedbackContact(item)) {
      showToast('No contact details on this feedback — can’t send a Google review request.', 'error');
      return;
    }
    setAskFor(item);
    setAskEmail(String(item.patientEmail || '').trim());
  };

  const sendAsk = async () => {
    if (!askFor) return;
    const email = askEmail.trim();
    if (!email && !askFor.patientEmail && !askFor.consentId) {
      showToast('Enter the patient’s email to send the Google review request.', 'error');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }

    setSendingId(askFor.id);
    try {
      const result = await reviewService.sendGoogleReviewAskFromFeedback({
        feedbackId: askFor.id,
        clinicId,
        clinicName,
        userData,
        to: email || undefined,
        patientName: askFor.patientName || undefined,
      });
      try {
        await reviewService.markFeedbackRead(askFor.id);
      } catch {
        /* non-blocking */
      }
      showToast(
        result.resend
          ? `Reminder sent to ${result.to}`
          : `Google review request sent to ${result.to}`
      );
      setAskFor(null);
      setAskEmail('');
    } catch (err) {
      showToast(err?.message || 'Could not send request', 'error');
    } finally {
      setSendingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      await reviewService.deleteFeedback(deleteFor.id);
      showToast('Feedback deleted');
      setDeleteFor(null);
    } catch (err) {
      showToast(err?.message || 'Could not delete feedback', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (!isReady) return null;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {toast ? (
        <div
          className={`px-5 py-2.5 text-sm ${
            toast.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
          }`}
        >
          {toast.message}
        </div>
      ) : null}

      <div className="border-b border-gray-100 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />
              Private patient feedback
              {unread > 0 ? (
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900">
                  {unread} new
                </span>
              ) : null}
            </h2>
            <p className="mt-1 text-sm text-gray-600 max-w-2xl">
              {audience === 'review-list'
                ? 'Private messages from patients you emailed from Review list.'
                : 'Messages patients send privately from your review page after a signed consent. Ask happy patients for a Google review, track opens and clicks, or delete old entries.'}
            </p>
          </div>
          <p className="text-sm text-gray-500 shrink-0">
            {filtered.length} of {scopedItems.length}
          </p>
        </div>

        <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, or message…"
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
            <FunnelIcon className="h-4 w-4 shrink-0 text-gray-400" />
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  filter === f.id
                    ? 'bg-slate-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-4 px-5 py-5 sm:px-6">
        {loading ? (
          <p className="py-10 text-center text-sm text-gray-500">Loading feedback…</p>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-6 py-12 text-center">
            <ChatBubbleLeftRightIcon className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-3 text-sm font-medium text-gray-700">
              {scopedItems.length === 0 ? 'No private feedback yet' : 'No matches for this filter'}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {scopedItems.length === 0
                ? audience === 'review-list'
                  ? 'When a Review list patient chooses “Send private feedback”, the message appears here.'
                  : 'When patients choose “Send private feedback” on your review page, messages appear here.'
                : 'Try another search or filter.'}
            </p>
          </div>
        ) : (
          filtered.map((item) => (
            <FeedbackCard
              key={item.id}
              item={item}
              expanded={expandedIds.has(item.id)}
              onToggleExpand={() =>
                setExpandedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(item.id)) next.delete(item.id);
                  else next.add(item.id);
                  return next;
                })
              }
              onAsk={openAskModal}
              onMarkRead={async (row) => {
                await reviewService.markFeedbackRead(row.id);
              }}
              onDelete={setDeleteFor}
              onPreview={setPreviewEmail}
              onOpenLog={setLogFor}
            />
          ))
        )}
      </div>

      {askFor ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !sendingId && setAskFor(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-lg font-semibold text-gray-900">Ask for a Google review</h3>
            <p className="mt-2 text-sm text-gray-600">
              We’ll email {askFor.patientName || 'the patient'} thanking them for their private
              feedback and include your Google review link.
            </p>
            <label className="mt-4 block text-sm font-medium text-gray-700">
              Patient email
              <input
                type="email"
                value={askEmail}
                onChange={(e) => setAskEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                autoComplete="email"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAskFor(null)}
                disabled={!!sendingId}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendAsk}
                disabled={!!sendingId}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
              >
                <StarIcon className="h-4 w-4" />
                {sendingId ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteFor ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !deleting && setDeleteFor(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-lg font-semibold text-red-700">Delete this feedback?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Remove the message from {deleteFor.patientName || 'Anonymous'}
              {deleteFor.patientEmail ? ` (${deleteFor.patientEmail})` : ''}. This cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteFor(null)}
                disabled={deleting}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                <TrashIcon className="h-4 w-4" />
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {logFor ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setLogFor(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-lg font-semibold text-gray-900">Email delivery log</h3>
            <p className="mt-1 text-sm text-gray-500">
              {logFor.patientName || 'Anonymous'}
              {logFor.patientEmail ? ` · ${logFor.patientEmail}` : ''}
            </p>
            <div className="mt-4 space-y-2">
              {sortDeliveryLog(logFor.emailDeliveryLog).map((entry, index) => {
                const previewHtml =
                  entry.html ||
                  (entry.trackingId === logFor.googleReviewAskedTrackingId
                    ? logFor.googleReviewAskedHtml
                    : null);
                return (
                  <div
                    key={`${entry.trackingId || index}-${entry.at?.seconds || index}`}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-gray-700"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">
                          {entry.resend ? 'Google review reminder' : 'Google review request'}
                        </p>
                        <p className="mt-0.5">To: {entry.to || '—'}</p>
                        {entry.subject ? (
                          <p className="mt-0.5 truncate text-gray-600" title={entry.subject}>
                            Subject: {entry.subject}
                          </p>
                        ) : null}
                        <p className="mt-0.5 text-gray-500">{shortDateTime(entry.at) || '—'}</p>
                      </div>
                      {previewHtml ? (
                        <button
                          type="button"
                          onClick={() =>
                            setPreviewEmail({
                              to: entry.to,
                              subject: entry.subject || 'Google review request',
                              html: previewHtml,
                              at: entry.at,
                            })
                          }
                          className="shrink-0 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700"
                        >
                          View email
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setLogFor(null)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {previewEmail ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setPreviewEmail(null)}
          role="presentation"
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="border-b border-gray-200 px-4 py-3">
              <h3 className="text-base font-semibold text-gray-900">Email preview</h3>
              <p className="mt-1 truncate text-xs text-gray-500">
                To: {previewEmail.to || '—'}
                {previewEmail.subject ? ` · ${previewEmail.subject}` : ''}
              </p>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100 p-3">
              <iframe
                title="Email preview"
                srcDoc={previewEmail.html}
                className="min-h-[420px] w-full rounded-lg border border-gray-200 bg-white"
                sandbox=""
              />
            </div>
            <div className="flex justify-end border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setPreviewEmail(null)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
